import React, { PropsWithChildren, useContext, useReducer } from "react";
import { Dispatch } from "react";
import { IOrganizationStudies, IStudy } from "../models/study.interface";
import studiesService from "../services/studies.service";

interface IManageStudiesState {
  loading: boolean;
  orgStudies: IOrganizationStudies;
  selectedStudy: IStudy | null;
  studyModal: {
    show: boolean;
    mode?: "add" | "edit";
  };
  showLinkUsersToStudyModal: boolean;
  showDeleteStudyModal: boolean;
}

export enum ManageStudiesActionType {
  SET_LOADING = "SET_LOADING",
  SET_ORG_STUDIES = "SET_ORG_STUDIES",
  SET_SELECTED_STUDY = "SET_SELECTED_STUDY",
  SET_STUDY_MODAL = "SET_STUDY_MODAL",
  SET_LINK_USERS_MODAL = "SET_LINK_USERS_MODAL",
  SET_DELETE_STUDY_MODAL = "SET_DELETE_STUDY_MODAL",
}

type ManageStudiesReducerAction =
  | {
      type: ManageStudiesActionType.SET_LOADING;
      payload: boolean;
    }
  | {
      type: ManageStudiesActionType.SET_ORG_STUDIES;
      payload: IOrganizationStudies;
    }
  | {
      type: ManageStudiesActionType.SET_SELECTED_STUDY;
      payload: IStudy | null;
    }
  | {
      type: ManageStudiesActionType.SET_STUDY_MODAL;
      payload: {
        show: boolean;
        mode?: "add" | "edit";
      };
    }
  | {
      type: ManageStudiesActionType.SET_LINK_USERS_MODAL;
      payload: boolean;
    }
  | {
      type: ManageStudiesActionType.SET_DELETE_STUDY_MODAL;
      payload: boolean;
    };

interface IManageStudiesContext {
  state: IManageStudiesState;
  dispatch: Dispatch<ManageStudiesReducerAction>;
  loadStudies: (opts?: { toggleLoading?: boolean }) => Promise<void>;
}

const initState: IManageStudiesState = {
  loading: false,
  orgStudies: {
    studies: [],
  },
  selectedStudy: null,
  showLinkUsersToStudyModal: false,
  studyModal: {
    show: false,
  },
  showDeleteStudyModal: false,
};

const ManageStudiesContext = React.createContext<IManageStudiesContext>({
  state: initState,
  dispatch: () => {},
  loadStudies: () => new Promise((resolve) => resolve()),
});

const manageStudiesReducer = (
  state: IManageStudiesState,
  action: ManageStudiesReducerAction
): IManageStudiesState => {
  switch (action.type) {
    case ManageStudiesActionType.SET_LOADING:
      return { ...state, loading: action.payload };
    case ManageStudiesActionType.SET_ORG_STUDIES:
      return {
        ...state,
        orgStudies: action.payload,
      };
    case ManageStudiesActionType.SET_SELECTED_STUDY:
      return { ...state, selectedStudy: action.payload };
    case ManageStudiesActionType.SET_STUDY_MODAL:
      return {
        ...state,
        studyModal: { ...state.studyModal, ...action.payload },
      };
    case ManageStudiesActionType.SET_LINK_USERS_MODAL:
      return { ...state, showLinkUsersToStudyModal: action.payload };
    case ManageStudiesActionType.SET_DELETE_STUDY_MODAL:
      return { ...state, showDeleteStudyModal: action.payload };
    default:
      return state;
  }
};

type ManageStudiesContextProviderProps = PropsWithChildren<{}>;

export const ManageStudiesContextProvider = ({
  children,
}: ManageStudiesContextProviderProps) => {
  const [state, dispatch] = useReducer(manageStudiesReducer, initState);

  const loadStudies = async (opts?: { toggleLoading?: boolean }) => {
    const { toggleLoading } = opts ?? { toggleLoading: true };

    try {
      if (toggleLoading)
        dispatch({
          type: ManageStudiesActionType.SET_LOADING,
          payload: true,
        });

      const orgStudies = await studiesService.getStudies();
      if (!orgStudies) {
        return;
      }

      dispatch({
        type: ManageStudiesActionType.SET_ORG_STUDIES,
        payload: orgStudies,
      });
    } finally {
      if (toggleLoading)
        dispatch({
          type: ManageStudiesActionType.SET_LOADING,
          payload: false,
        });
    }
  };
  return (
    <ManageStudiesContext.Provider value={{ state, dispatch, loadStudies }}>
      {children}
    </ManageStudiesContext.Provider>
  );
};

export const useManageStudiesContext = () => useContext(ManageStudiesContext);
